import { dateDmyToYmd } from '@/utils';
import { onlyAlphanumerics } from '@/utils/string';

import {
  FormAddress,
  FormRisk,
  FormRiskTravel,
  FormTruck,
  FormTruckCompositionOwner,
  FormTrucker,
  RiskAddressModel,
  RiskCnhModel,
  RiskModel,
  RiskTravelModel,
  RiskTruckCompositionModel,
  RiskTruckCompositionOwnerModel,
  RiskTruckModel,
  RiskTruckerModel,
} from '@/types';

const getParsedCnh = (cnh: RiskCnhModel): RiskCnhModel => ({
  ...cnh,
  dateFirstLicense: dateDmyToYmd(cnh.dateFirstLicense),
  expirationDate: dateDmyToYmd(cnh.expirationDate),
});

const getAddress = (address: FormAddress): RiskAddressModel => {
  const { location, ...restOfAddress } = address;

  return { city: location.name, state: location.stateAbbr, ...restOfAddress };
};

const getParsedTrucker = (trucker: FormTrucker): RiskTruckerModel => {
  const { address, birthday, cnh, ...restOfData } = trucker;

  return { birthday: dateDmyToYmd(birthday), cnh: getParsedCnh(cnh), address: getAddress(address), ...restOfData };
};

const getCompositionOwner = (owner: FormTruckCompositionOwner): RiskTruckCompositionOwnerModel => {
  const { address, birthdate, motherName, rntrcCpfCnpj, ...restOfData } = owner;

  return {
    address: getAddress(address),
    rntrcCpfCnpj: rntrcCpfCnpj.replace(/[.-]/g, ''),
    motherName: motherName || '',
    birthdate: !!birthdate ? dateDmyToYmd(birthdate) : null,
    ...restOfData,
  };
};

const getParsedTruck = (truck: FormTruck, trucker: FormTrucker): RiskTruckModel => {
  const { compositions, ...restOfData } = truck;

  const localCompositions: RiskTruckCompositionModel[] = compositions.map((item) => {
    const { owner, chassis, location, vehicleIsLeased, leaserCpfCnpj, leaserName, ...restOfItem } = item;
    const { name: city, stateAbbr: state } = location;

    return {
      city,
      state,
      chassis: onlyAlphanumerics(chassis),
      owner: getCompositionOwner(owner),
      vehicleIsLeased: vehicleIsLeased || false,
      leaserCpfCnpj: leaserCpfCnpj || '',
      leaserName: leaserName || '',
      ...restOfItem,
      truckerIsOwner: owner.cpfCnpj === trucker.cpf,
    };
  });

  return { ...restOfData, compositions: localCompositions };
};

export const getParsedTravel = (travel: FormRiskTravel, freightId: string): RiskTravelModel => {
  const { originPlace, destinationPlace, ...restOfData } = travel;

  return {
    ...restOfData,
    originCity: originPlace.name,
    originState: originPlace.stateAbbr,
    destinationCity: destinationPlace.name,
    destinationState: destinationPlace.stateAbbr,
    freightId,
  };
};

export const parseFormToRiskModel = (form: FormRisk, freightId: string = null): RiskModel => {
  const { trucker, truck, partnerId, travel, uuid = '' } = form;

  return {
    uuid,
    truck: getParsedTruck(truck, trucker),
    trucker: getParsedTrucker(trucker),
    travel: getParsedTravel(travel, freightId),
    partnerId,
  };
};
